import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/AuthLayout';
import AuthField from '../components/AuthField';
import FormError from '../components/FormError';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '', firstName: '', lastName: '', businessName: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const inviteToken = new URLSearchParams(location.search).get('invite');

  const updateField = (name, value) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
    setFieldErrors((previous) => ({ ...previous, [name]: '', ...(name === 'password' ? { confirmPassword: '' } : {}) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'Enter your first name.';
    if (!formData.lastName.trim()) errors.lastName = 'Enter your surname.';
    if (!formData.email.trim() || !e.currentTarget.elements['register-email'].validity.valid) errors.email = 'Enter a valid email address.';
    if (formData.password.length < 6) errors.password = 'Use at least 6 characters.';
    if (!formData.confirmPassword) errors.confirmPassword = 'Confirm your password.';
    else if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match.';
    setFieldErrors(errors);
    if (Object.keys(errors).length) {
      document.getElementById(`register-${Object.keys(errors)[0]}`)?.focus();
      return;
    }
    setLoading(true);
    try {
      await register({
        email: formData.email.trim(),
        password: formData.password,
        fullName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        businessName: inviteToken ? '' : formData.businessName.trim(),
        inviteToken: inviteToken || undefined,
      });
      navigate('/');
    } catch (err) {
      const data = err.response?.data;
      setError(!err.response
        ? 'Cannot reach the server. Please check your connection and try again.'
        : err.response.status >= 500
          ? 'Account creation is temporarily unavailable. Please try again shortly.'
          : data?.error || (Array.isArray(data?.errors) && data.errors[0]?.msg) || 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = (name) => ({
    id: `register-${name}`, value: formData[name], error: fieldErrors[name],
    onChange: (e) => updateField(name, e.target.value), disabled: loading, required: true,
  });

  return (
    <AuthLayout title={inviteToken ? 'Join your team' : 'Create your account'} legalLinksNewTab
      description={inviteToken ? 'Use your invited email to join your team’s existing workspace.' : 'Create your account and a workspace for your business. You’ll be its owner.'}>
      {!inviteToken && (
        <aside className="mb-5 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm">
          <p className="font-semibold text-primary-800">7-day free trial · No credit card required</p>
          <p className="mt-1 text-xs leading-5 text-gray-600">Then Core CRM is US$15/month. AI plans from US$50/month.</p>
        </aside>
      )}
      <form onSubmit={handleSubmit} noValidate className="space-y-4" aria-busy={loading}>
        <FormError message={error} />
        <div className="grid grid-cols-1 gap-4 min-[360px]:grid-cols-2">
          <AuthField {...fieldProps('firstName')} label="First name" type="text" autoComplete="given-name" />
          <AuthField {...fieldProps('lastName')} label="Surname" type="text" autoComplete="family-name" />
        </div>
        {!inviteToken && <AuthField {...fieldProps('businessName')} label={<>Business name <span className="font-normal text-gray-500">(optional)</span></>}
          type="text" autoComplete="organization" required={false} hint="This becomes your workspace’s starting name. You can rename it later." />}
        <AuthField {...fieldProps('email')} label="Email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="you@example.com" />
        <AuthField {...fieldProps('password')} label="Password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
          hint="Use at least 6 characters. A longer password is more secure." minLength={6}
          showPassword={showPassword} onTogglePassword={() => setShowPassword(!showPassword)} />
        <AuthField {...fieldProps('confirmPassword')} label="Confirm password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" />
        <button data-testid="register-submit" type="submit" disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 p-3 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60">
          {loading && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}
          <span>{loading ? 'Creating account...' : inviteToken ? 'Create account and join team' : 'Create account & workspace'}</span>
        </button>
      </form>
      <p role="status" className="sr-only">{loading ? 'Creating account...' : ''}</p>
      {!inviteToken && <p className="mt-4 text-sm leading-6 text-gray-600">Joining an existing team? Ask its owner for an invitation and register from that email. Each account can belong to one workspace at a time.</p>}

      <p className="mt-5 border-t border-gray-200 pt-3 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <Link to={inviteToken ? `/login?invite=${encodeURIComponent(inviteToken)}` : '/login'} className="inline-flex items-center font-semibold text-primary-600 hover:text-primary-700">Sign in</Link>
      </p>
    </AuthLayout>
  );
};

export default Register;
